import React from "react";
import { AiOutlineExclamation } from "react-icons/ai";
import TextField from "@mui/material/TextField";
import { useRouter } from "next/router";
import useLoginForm from "hooks/auth/useLoginForm";
import useAuth from "hooks/auth/useAuth";
import { MODES } from "./constants";
import { NOOP } from "libs/utils";
import useDarkMode from "hooks/useDarkMode";

const Login = ({ onClose = NOOP }) => {
  const router = useRouter();
  const { loginWithEmailAndPassword, loginWithGoogle, loginWithFacebook } =
    useAuth();
  const { values, errors, submitForm, setFieldValue } = useLoginForm(
    loginWithEmailAndPassword,
    onClose,
  );
  const { theme } = useDarkMode();
  const isDark = theme === "dark";

  return (
    <div
      className={`flex flex-col gap-[18px] p-[24px] rounded-[24px] w-full transition-all duration-300 ${
        isDark
          ? "bg-[#030014]/60 border border-cyan-500/10 shadow-[inset_0_0_30px_rgba(102,133,255,0.03)]"
          : "bg-white/80 border border-cyan-600/15 shadow-[0_4px_30px_rgba(8,145,178,0.08)] backdrop-blur-sm"
      }`}
    >
      {/* Header */}
      <div className="flex flex-col gap-[8px]">
        <span
          className={`text-[32px] font-sans font-black tracking-tighter leading-none ${
            isDark
              ? "text-cyan-50 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
              : "text-cyan-900"
          }`}
        >
          Մուտք
        </span>
        <div
          className={`h-[2px] w-[107px] rounded-full ${
            isDark
              ? "bg-gradient-to-r from-cyan-500/60 to-transparent"
              : "bg-gradient-to-r from-cyan-600/50 to-transparent"
          }`}
        />
      </div>

      {/* Email Field */}
      <TextField
        placeholder="Էլ․ հասցե"
        type="email"
        fullWidth
        autoComplete="off"
        value={values.email}
        onChange={(e) => setFieldValue("email", e.target.value)}
      />

      {/* Password Field */}
      <div className="flex flex-col gap-[8px]">
        <TextField
          placeholder="Գաղտնաբառ"
          autoComplete="new-password"
          type="password"
          fullWidth
          value={values.password}
          onChange={(e) => setFieldValue("password", e.target.value)}
        />
        <div className="flex justify-end gap-[8px]">
          <span
            className={`text-[14px] font-[400] ${
              isDark ? "text-slate-400" : "text-slate-500"
            }`}
          >
            մոռացե՞լ եք գաղտնաբառը`
          </span>
          <button
            className={`text-[14px] font-[600] underline cursor-pointer transition-colors ${
              isDark
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
            }`}
            onClick={() =>
              router.push(
                {
                  pathname: "/auth",
                  query: { mode: MODES.RESET },
                },
                undefined,
                { shallow: true },
              )
            }
          >
            վերականգնել
          </button>
        </div>
      </div>

      {/* Terms notice */}
      <div
        className={`flex justify-start items-center p-[12px] gap-[10px] rounded-[14px] ${
          isDark
            ? "bg-cyan-950/20 border border-cyan-500/10"
            : "bg-cyan-50 border border-cyan-200/70"
        }`}
      >
        <div
          className={`w-[40px] h-[40px] flex items-center justify-center rounded-[12px] shrink-0 ${
            isDark
              ? "bg-cyan-900/30 text-cyan-400"
              : "bg-cyan-100 text-cyan-600"
          }`}
        >
          <AiOutlineExclamation size={18} />
        </div>
        <span
          className={`text-[13px] w-fit leading-relaxed ${
            isDark ? "text-slate-300" : "text-slate-600"
          }`}
        >
          Մուտք գործելով՝ դուք համաձայնվում եք մեր{" "}
          <button
            onClick={() => router.push("/privacy-policy")}
            className={`underline cursor-pointer mx-1 transition-colors ${
              isDark
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
            }`}
          >
            գաղտնիության քաղաքականությանը
          </button>
          <span> և ընդունում եք </span>
          <button
            onClick={() => router.push("/terms-of-use")}
            className={`underline cursor-pointer ml-1 transition-colors ${
              isDark
                ? "text-cyan-400 hover:text-cyan-300"
                : "text-cyan-600 hover:text-cyan-700"
            }`}
          >
            պայմաններն ու դրույթները
          </button>
        </span>
      </div>

      {/* Submit */}
      <button
        onClick={submitForm}
        className={`rounded-[14px]  tracking-widest uppercase text-[16px] font-[700] flex items-center justify-center px-[24px] h-[60px] cursor-pointer transition-all duration-300 ${
          isDark
            ? "bg-cyan-500/20 border border-cyan-400 text-cyan-50 shadow-[0_0_15px_rgba(102,133,255,0.2)] hover:bg-cyan-500/30 hover:shadow-[0_0_25px_rgba(102,133,255,0.4)]"
            : "bg-cyan-600 border border-cyan-700 text-white shadow-[0_4px_15px_rgba(8,145,178,0.3)] hover:bg-cyan-700 hover:shadow-[0_4px_20px_rgba(8,145,178,0.4)]"
        }`}
      >
        <span>Մուտք գործել</span>
      </button>

      {/* Errors */}
      {Boolean(errors.email) && (
        <div
          className={`flex justify-start items-center p-[12px] gap-[10px] rounded-[14px] ${
            isDark
              ? "bg-red-500/10 border border-red-500/30"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <div
            className={`w-[40px] h-[40px] flex items-center justify-center rounded-[12px] shrink-0 ${
              isDark ? "text-red-400 bg-red-950/50" : "text-red-500 bg-red-100"
            }`}
          >
            <AiOutlineExclamation size={18} />
          </div>
          <span
            className={`text-[14px] w-fit ${isDark ? "text-red-200" : "text-red-600"}`}
          >
            {errors.email}
          </span>
        </div>
      )}
      {Boolean(errors.password) && (
        <div
          className={`flex justify-start items-center p-[12px] gap-[10px] rounded-[14px] ${
            isDark
              ? "bg-red-500/10 border border-red-500/30"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <div
            className={`w-[40px] h-[40px] flex items-center justify-center rounded-[12px] shrink-0 ${
              isDark ? "text-red-400 bg-red-950/50" : "text-red-500 bg-red-100"
            }`}
          >
            <AiOutlineExclamation size={18} />
          </div>
          <span
            className={`text-[14px] w-fit ${isDark ? "text-red-200" : "text-red-600"}`}
          >
            {errors.password}
          </span>
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center justify-center gap-[18px]">
        <div
          className={`flex-1 h-[1px] ${isDark ? "bg-gradient-to-r from-transparent via-cyan-500/25 to-transparent" : "bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent"}`}
        />
        <span
          className={` text-[13px] shrink-0 ${isDark ? "text-cyan-400/70" : "text-cyan-600/70"}`}
        >
          կամ
        </span>
        <div
          className={`flex-1 h-[1px] ${isDark ? "bg-gradient-to-r from-transparent via-cyan-500/25 to-transparent" : "bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent"}`}
        />
      </div>

      {/* Social Buttons */}
      <div className="flex justify-center">
        <div
          className={`p-[12px] flex items-center gap-[12px] rounded-[24px] ${
            isDark
              ? "bg-cyan-950/20 border border-cyan-500/10"
              : "bg-slate-50 border border-slate-200"
          }`}
        >
          <button
            onClick={() => loginWithFacebook().then(() => onClose())}
            className={`flex items-center justify-center rounded-[12px] p-[10px] cursor-pointer transition-all ${
              isDark
                ? "bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20"
                : "bg-blue-50 hover:bg-blue-100 border border-blue-200"
            }`}
            title="Facebook-ով մուտք"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                d="M17.5 4H15.5C13 4 11.5 5.5 11.5 8V20"
                stroke={isDark ? "#6685FF" : "#1877F2"}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.5 12H15.5"
                stroke={isDark ? "#6685FF" : "#1877F2"}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            onClick={() => loginWithGoogle().then(() => onClose())}
            className={`flex items-center justify-center rounded-[12px] p-[10px] cursor-pointer transition-all ${
              isDark
                ? "bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20"
                : "bg-white hover:bg-slate-50 border border-slate-200 shadow-sm"
            }`}
            title="Google-ով մուտք"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                d="M21.85 11.1H12.68V13.83H19.19C18.86 17.64 15.69 19.27 12.69 19.27C8.86003 19.27 5.50003 16.25 5.50003 12C5.50003 7.9 8.70003 4.73 12.7 4.73C15.79 4.73 17.6 6.7 17.6 6.7L19.5 4.72C19.5 4.72 17.06 2 12.6 2C6.92003 2 2.53003 6.8 2.53003 12C2.53003 17.05 6.66003 22 12.75 22C18.1 22 22 18.33 22 12.91C22 11.76 21.85 11.1 21.85 11.1Z"
                fill={isDark ? "#6685FF" : "#4285F4"}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Register link */}
      <div className="flex items-center justify-center gap-[18px]">
        <span
          className={`text-[12px] md:text-[16px] font-[400] ${
            isDark ? "text-slate-400" : "text-slate-500"
          }`}
        >
          գրանցված չէ՞ք
        </span>
        <button
          className={`flex items-center justify-center px-[28px] py-[8px] rounded-[80px] h-[60px] cursor-pointer text-center transition-all duration-300 ${
            isDark
              ? "bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20"
              : "bg-cyan-50 border border-cyan-300 hover:bg-cyan-100"
          }`}
          onClick={() =>
            router.push(
              {
                pathname: "/auth",
                query: { mode: MODES.REGISTRATION },
              },
              undefined,
              { shallow: true },
            )
          }
        >
          <span
            className={`text-[12px] md:text-[16px] font-[700] uppercase  tracking-widest ${
              isDark ? "text-cyan-400" : "text-cyan-700"
            }`}
          >
            Գրանցվել
          </span>
        </button>
      </div>
    </div>
  );
};

export default Login;
